import { formatGr } from '@odhaczka/core';
import type { Purchase } from '../../data/history';

const W = 320;
const H = 150;
const PAD = { left: 44, right: 8, top: 10, bottom: 22 };

const shortDate = (ms: number) => new Date(ms).toLocaleDateString('pl-PL', { month: 'short', year: '2-digit' });

/** Wykres ceny: półkowa (linia schodkowa) i zapłacona (kropki), oś czasu od pierwszego do ostatniego zakupu. */
export function PriceChart({ purchases, perKg = false }: { purchases: readonly Purchase[]; perKg?: boolean }) {
  const pts = [...purchases].sort((a, b) => a.ms - b.ms);
  if (pts.length === 0) return null;
  const t0 = pts[0]!.ms;
  const t1 = Math.max(pts.at(-1)!.ms, t0 + 86_400_000);
  const values = pts.flatMap((p) => [p.shelfGr, p.paidGr]);
  let lo = Math.min(...values);
  let hi = Math.max(...values);
  if (hi === lo) {
    lo = Math.max(0, lo - 100);
    hi += 100;
  }
  const pad = (hi - lo) * 0.1;
  lo = Math.max(0, lo - pad);
  hi += pad;
  const x = (ms: number) => PAD.left + ((ms - t0) / (t1 - t0)) * (W - PAD.left - PAD.right);
  const y = (gr: number) => PAD.top + (1 - (gr - lo) / (hi - lo)) * (H - PAD.top - PAD.bottom);

  // Cena półkowa obowiązuje do następnego zakupu: schodki.
  let path = '';
  pts.forEach((p, i) => {
    path += i === 0 ? `M${x(p.ms)},${y(p.shelfGr)}` : `H${x(p.ms)}V${y(p.shelfGr)}`;
  });
  path += `H${x(t1)}`;
  const unit = perKg ? '/kg' : '';

  return (
    <figure className="flex flex-col gap-1">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label={`Wykres ceny, ${pts.length} zakupów`}>
        {[lo, (lo + hi) / 2, hi].map((v) => (
          <g key={v}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y(v)} y2={y(v)} className="stroke-stone-200 dark:stroke-stone-800" strokeWidth={1} />
            <text x={PAD.left - 4} y={y(v) + 3} textAnchor="end" className="fill-stone-500 text-[9px]">
              {formatGr(Math.round(v), false)}
            </text>
          </g>
        ))}
        <path d={path} fill="none" className="stroke-brand-600" strokeWidth={2} />
        {pts.map((p) =>
          p.paidGr < p.shelfGr ? <circle key={p.lineId} cx={x(p.ms)} cy={y(p.paidGr)} r={3} className="fill-amber-500" /> : null,
        )}
        {pts.map((p) => (
          <circle key={`s${p.lineId}`} cx={x(p.ms)} cy={y(p.shelfGr)} r={2.5} className="fill-brand-700" />
        ))}
        <text x={PAD.left} y={H - 6} className="fill-stone-500 text-[9px]">
          {shortDate(t0)}
        </text>
        <text x={W - PAD.right} y={H - 6} textAnchor="end" className="fill-stone-500 text-[9px]">
          {shortDate(t1)}
        </text>
      </svg>
      <figcaption className="flex gap-4 px-1 text-xs text-stone-500">
        <span className="flex items-center gap-1">
          <span className="inline-block h-0.5 w-4 bg-brand-600" /> cena półkowa{unit}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block size-2 rounded-full bg-amber-500" /> zapłacona (z rabatem)
        </span>
      </figcaption>
    </figure>
  );
}
